import { Alert, Card, Group, Loader, SimpleGrid, Stack, Text, Title } from '@mantine/core';
import { useQueries } from '@tanstack/react-query';
import { Link } from 'react-router-dom';

import { unwrap } from '../../api/client';
import { childProgress } from '../../api/gen';
import { ChildAvatar } from '../../components/ChildAvatar';
import { useOrg, useSession } from '../../lib/session';

// What a parent wants in five seconds: who has been working, and how it is
// going. Deliberately not a leaderboard between siblings — one card each, and
// the numbers are about effort rather than rank.
export function OverviewPage() {
  const { pairing } = useSession();
  const org = useOrg();
  const children = pairing?.children ?? [];

  const progress = useQueries({
    queries: children.map((c) => ({
      queryKey: ['progress', c.id],
      queryFn: () => unwrap(childProgress({ path: { childId: c.id }, headers: org })),
    })),
  });

  if (children.length === 0) {
    return (
      <Alert variant="light">
        Noch kein Kinderprofil. Lege unter <Link to="/eltern/kinder">Kinder</Link> das erste an.
      </Alert>
    );
  }

  return (
    <Stack>
      <Title order={3}>Überblick</Title>
      <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }}>
        {children.map((c, i) => {
          const q = progress[i];
          const p = q?.data;
          return (
            <Card key={c.id} withBorder padding="lg" component={Link} to={`/eltern/kinder/${c.id}`}>
              <Stack gap="sm">
                <Group gap="sm">
                  <ChildAvatar name={c.name} mode={c.mode} size={40} />
                  <div>
                    <Text fw={700}>{c.name}</Text>
                    <Text size="xs" c="dimmed">
                      {p?.badge ?? '—'}
                    </Text>
                  </div>
                </Group>

                {q?.isLoading ? (
                  <Loader size="xs" />
                ) : (
                  <Group gap="xl">
                    <Figure label="Sitzungen" value={p?.sessions ?? 0} />
                    <Figure label="selbst gelöst" value={p?.solved ?? 0} />
                    <Figure label="Tage in Folge" value={p?.streak ?? 0} />
                  </Group>
                )}

                {p?.nextBadge ? (
                  <Text size="xs" c="dimmed">
                    Noch {p.solvedToNext} bis „{p.nextBadge}“
                  </Text>
                ) : null}
              </Stack>
            </Card>
          );
        })}
      </SimpleGrid>
    </Stack>
  );
}

function Figure({ label, value }: { label: string; value: number }) {
  return (
    <div>
      <Text fw={700} size="xl">
        {value}
      </Text>
      <Text size="xs" c="dimmed">
        {label}
      </Text>
    </div>
  );
}
